import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { PlatformService } from 'app/core/platform/platform.service';
import { PlatformSettings } from 'app/core/platform/platform.types';
import { delay, scan, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CdnFileType, ImageCdnFileType, ImageFile } from 'app/shared/shared.types';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Track } from 'app/modules/user/releases/releases.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { HashMap, translate } from '@ngneat/transloco';
import { SalesReportFile } from 'app/modules/user/payments/payments.types';
import { fuseAnimations } from '@fuse/animations';

export interface UploadStatus {
    progress: number
    state: 'PENDING' | 'IN_PROGRESS' | 'PROCESSING' | 'DONE',
    response?: ImageFile | Track | SalesReportFile;
}

@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    animations: fuseAnimations,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true
        }
    ]
})
export class FileUploadComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @ViewChild('fileInput') fileInput: HTMLInputElement;
    @Input() text: string = 'Upload';
    @Input() styling: string = '';
    @Input() fileType: CdnFileType;

    @Input() square?: boolean;
    @Input() minWidth?: number;
    @Input() maxWidth?: number;
    @Input() minHeight?: number;
    @Input() maxHeight?: number;
    @Input() forUser?: User;

    /** Only for uploaded tracks */
    @Input() parentUuid: string;

    platform: PlatformSettings;
    backgroundImage: string;
    file: ImageFile | Track;
    acceptTypes: string[];
    disabled: boolean;

    readonly initialUploadStatus: UploadStatus = { progress: 0, state: 'PENDING' };
    uploadStatus: UploadStatus | undefined;
    processing: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _platformService: PlatformService,
        private _httpClient: HttpClient,
        private _fuseConfirmationService: FuseConfirmationService,
    ) {
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    onChange = (_: any) => {
    };

    onTouched = () => {
    };

    setFile(file: ImageFile | Track | SalesReportFile, updateFromModel?: boolean) {
        this.file = file;
        this.processing = false;

        if (file && this.isImageFile(file)) {
            this.backgroundImage = this._platformService.getFileSrc(file, this.fileType);
        }
        if (!updateFromModel) {
            this.onChange(file);
        }
        this._changeDetectorRef.markForCheck();
    }

    ngOnInit(): void {
        this._platformService.platform$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(platform => {
                this.platform = platform;
                if (this.fileType === 'track') {
                    this.acceptTypes = platform.audioMimeTypes;
                } else if (this.fileType === 'favicon') {
                    this.acceptTypes = platform.faviconMimeTypes;
                } else if (this.fileType === 'salesreport') {
                    this.acceptTypes = platform.salesReportMimeTypes;
                } else {
                    this.acceptTypes = platform.imageMimeTypes;
                }
            });
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    writeValue(value: ImageFile | Track): void {
        this.setFile(value, true);
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }

    onContainerClick() {
        this.onTouched();
    }

    openErrorModal(errorKey, params?: HashMap) {
        const dialogRef = this._fuseConfirmationService.open({
            'title': 'Upload Error',
            'message': translate(errorKey, params),
            'icon': {
                'show': true,
                'name': 'heroicons_outline:exclamation-triangle',
                'color': 'error'
            },
            'actions': {
                'confirm': {
                    'show': true,
                    'label': translate('close'),
                    'color': 'primary'
                },
                'cancel': {
                    'show': false
                }
            },
            'dismissible': true
        });
    }

    async uploadFile(fileList: FileList) {
        if (!fileList.length) {
            return;
        }

        const allowedTypes = this.acceptTypes;
        const file = fileList[0];

        if (allowedTypes.length && !allowedTypes.includes(file.type)) {
            console.error(file.type + " is not allowed");
            console.info("Allowed types: " + allowedTypes.join(", "));
            this.openErrorModal("error-image-disallowed-file-type");
            return;
        }

        if (this.square || this.minWidth || this.minHeight) {
            let requirementsResult = await this.checkImageRequirements(file);
            if (!requirementsResult.success) {
                this.openErrorModal(requirementsResult.errorKey);
                return;
            }
        }

        this.processing = false;

        const data = new FormData();
        data.append('file', file);
        if (this.parentUuid) {
            data.append('parent', this.parentUuid);
        }
        if (this.forUser) {
            data.append('user', this.forUser.uuid);
        }
        this.uploadStatus = this.initialUploadStatus;
        this._httpClient.post('api/upload/' + this.fileType, data, {
            reportProgress: true,
            observe: 'events',
        })
            .pipe(
                scan(this.calculateUploadStatus, this.initialUploadStatus),
            )
            .subscribe({
                next: status => {
                    this.uploadStatus = status;
                    this.processing = status.state === 'PROCESSING';

                    if (status.state === 'DONE') {
                        this.setFile(status.response);
                        this.uploadStatus = undefined;
                    }
                    this._changeDetectorRef.markForCheck();
                },
                error: err => {
                    console.log(err);
                    if (err.status === 409) {
                        this.openErrorModal("error-file-already-exists");
                    } if (err.error.error === 'track-already-in-release') {
                        let params = {title: err.error.track.title ? err.error.track.title : 'Untitled Track', order: err.error.track.order};
                        this.openErrorModal("error-track-already-in-release", params);
                    } else {
                        this.openErrorModal("error-unknown-upload-error");
                    }

                    this.processing = false;
                    this.uploadStatus = undefined;
                    this._changeDetectorRef.markForCheck();
                }
    });
    }

    async checkImageRequirements(file: File): Promise<{success: boolean, errorKey: string}> {
        const _URL = window.URL || window.webkitURL;
        var objectUrl = _URL.createObjectURL(file);

        let parserPromise = new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                let result = {success: true, errorKey: null};
                if (this.square) {
                    if (img.width !== img.height) {
                        result.success = false;
                        result.errorKey = "error-image-not-square";
                    }
                }

                if ((this.minWidth && this.minWidth > img.width) || (this.minHeight && this.minHeight > img.height)) {
                    result.success = false;
                    result.errorKey = "error-image-too-small";
                }

                if ((this.maxWidth && this.maxWidth < img.width) || (this.maxHeight && this.maxHeight < img.height)) {
                    result.success = false;
                    result.errorKey = "error-image-too-large";
                }

                _URL.revokeObjectURL(objectUrl);
                resolve(result);
            };
            img.src = objectUrl;
        });

        let timeoutPromise = new Promise((res) => setTimeout(() => {
            _URL.revokeObjectURL(objectUrl);
            res({success: false, errorKey: "error-image-upload-timeout"});
        }, 5000));

        return Promise.race([parserPromise, timeoutPromise]) as Promise<{success: boolean, errorKey: string}>;
    }

    validate(c: FormControl) {
        if (c.hasValidator(Validators.required) && !this.file) {
            return { invalid: true };
        }
    }

    private calculateUploadStatus(upload: UploadStatus, event: HttpEvent<any>): UploadStatus {
        if (event.type === HttpEventType.UploadProgress) {
            let progress = event.total ? Math.round((100 * event.loaded) / event.total) : upload.progress;
            return {
                progress: progress,
                state: progress !== 100 ? 'IN_PROGRESS' : 'PROCESSING',
            }
        } else if (event.type === HttpEventType.Response) {
            return {
                progress: 100,
                state: 'DONE',
                response: event.body,
            }
        }
        return upload;
    }

    private isImageFile(file: any): file is ImageFile {
        return ["logo", "favicon", "artwork", "avatar"].includes(file.type);
    }
}
